import type { WeatherHourly } from "./daily-obs-types";

type MtbSoilMoistureLayers = Pick<
  WeatherHourly,
  | "soil_moisture_0_1cm"
  | "soil_moisture_1_3cm"
  | "soil_moisture_3_9cm"
  | "soil_moisture_9_27cm"
>;

/**
 * PeakWave's globally calibrated MTB soil-moisture heuristic.
 *
 * These absolute VWC thresholds are not universal trail limits. Real traction,
 * drainage and damage risk vary with soil texture, compaction, construction and
 * local hydrology. Keep scorer and display consumers on this shared model until
 * PeakWave has the trail-specific data needed for normalisation.
 */
export const MTB_SOIL_MOISTURE_MODEL = {
  sweetSpot: { low: 0.08, high: 0.16 },
  layerWeights: [0.50, 0.30, 0.15, 0.05] as const,
  curve: {
    drySigma: 0.025,
    wetSigma: 0.11,
    dryFloor: 0.55,
    wetFloor: 0.08,
  },
  missing: {
    dryFactor: 0.95,
    proxyMaxMoisture: 0.65,
    activeRainFullMm: 4,
  },
  labels: {
    dustyBelow: 0.06,
    wetFrom: 0.20,
    muddyFrom: 0.25,
    boggyFrom: 0.30,
    waterloggedFrom: 0.35,
  },
  trailDamage: {
    // Existing model boundaries retained by audit fixtures MTB-M3 and MTB-M4.
    amberAbove: 0.25,
    redAbove: 0.35,
    // Audit fixture MTB-M5: 10 mm/h active heavy rain cannot be called
    // trail-friendly. No lower global rain boundary is validated.
    activeRainRedMm: 10,
  },
  trailDrying: {
    rideableAtOrBelow: 0.22,
    waterloggedAtOrAbove: 0.45,
  },
} as const;

export type MtbSoilMoistureLabel =
  | "Dusty"
  | "Dry"
  | "Tacky"
  | "Damp"
  | "Wet"
  | "Muddy"
  | "Boggy"
  | "Waterlogged";

export type MtbLocalDrainageConfidence = "low" | "medium" | "high";
export type MtbTrailVulnerabilityEvidenceSource =
  | "modelled-forecast"
  | "local-config"
  | "unavailable";

export type MtbTrailVulnerabilityEvidence<T> = {
  value: T | null;
  source: MtbTrailVulnerabilityEvidenceSource;
};

export type MtbTrailVulnerabilityInput = {
  modelledSoilMoisture: number | null;
  activeRainMm: number | null;
  antecedentRainMm: number | null;
  freezeThaw: boolean | null;
  surface: string | null;
  localDrainageConfidence: MtbLocalDrainageConfidence | null;
};

export type MtbTrailVulnerabilityResult = {
  availability: "available" | "partial" | "unavailable";
  severity: "green" | "amber" | "red" | null;
  label: "Trail-friendly" | "Likely vulnerable" | "Vulnerability unavailable";
  detail: string;
  confidence: "local" | "modelled" | "limited";
  provenance: {
    modelledSoilMoisture: MtbTrailVulnerabilityEvidence<number>;
    activeRain: MtbTrailVulnerabilityEvidence<number>;
    antecedentWetness: MtbTrailVulnerabilityEvidence<number>;
    freezeThaw: MtbTrailVulnerabilityEvidence<boolean>;
    surface: MtbTrailVulnerabilityEvidence<string>;
    localDrainage: MtbTrailVulnerabilityEvidence<MtbLocalDrainageConfidence>;
  };
};

/**
 * Model trail vulnerability without claiming operational authority.
 *
 * Weather and global soil fields can indicate likely damage risk, but only a
 * separately sourced operator status may say that a trail is open or closed.
 */
export function assessMtbTrailVulnerability(
  input: MtbTrailVulnerabilityInput,
): MtbTrailVulnerabilityResult {
  const thresholds = MTB_SOIL_MOISTURE_MODEL.trailDamage;
  const red =
    (input.modelledSoilMoisture != null
      && input.modelledSoilMoisture > thresholds.redAbove)
    || (input.activeRainMm != null
      && input.activeRainMm >= thresholds.activeRainRedMm);
  const amber =
    red
    || (input.modelledSoilMoisture != null
      && input.modelledSoilMoisture > thresholds.amberAbove)
    || input.freezeThaw === true;
  const hasCoreModelEvidence =
    input.modelledSoilMoisture != null && input.freezeThaw != null;
  const hasActionableHazardEvidence = red || amber;
  const hasLocalEvidence =
    input.surface != null && input.localDrainageConfidence != null;
  const availability = hasCoreModelEvidence
    ? hasLocalEvidence ? "available" : "partial"
    : hasActionableHazardEvidence ? "partial" : "unavailable";
  const severity = availability === "unavailable"
    ? null
    : red ? "red" : amber ? "amber" : "green";

  return {
    availability,
    severity,
    label: availability === "unavailable"
      ? "Vulnerability unavailable"
      : severity === "green"
        ? "Trail-friendly"
        : "Likely vulnerable",
    detail: availability === "unavailable"
      ? "Trail vulnerability cannot be modelled from the available inputs. Check the operator status and conditions on site."
      : severity === "red"
        ? "Weather and soil estimates indicate a high risk of rutting or surface damage. Check the operator status and conditions on site."
        : severity === "amber"
          ? "Weather and soil estimates indicate that the surface may be vulnerable. Avoid skidding and check conditions on site."
          : hasLocalEvidence
            ? "The available weather, soil and local trail context do not indicate elevated damage risk."
            : "The available weather and soil model do not indicate elevated damage risk. Local surface and drainage are unavailable, so check conditions on site.",
    confidence: availability === "unavailable"
      ? "limited"
      : hasLocalEvidence
        ? "local"
        : hasCoreModelEvidence
          ? "modelled"
          : "limited",
    provenance: {
      modelledSoilMoisture: modelEvidence(input.modelledSoilMoisture),
      activeRain: modelEvidence(input.activeRainMm),
      antecedentWetness: modelEvidence(input.antecedentRainMm),
      freezeThaw: modelEvidence(input.freezeThaw),
      surface: localEvidence(input.surface),
      localDrainage: localEvidence(input.localDrainageConfidence),
    },
  };
}

function modelEvidence<T>(value: T | null): MtbTrailVulnerabilityEvidence<T> {
  return {
    value,
    source: value == null ? "unavailable" : "modelled-forecast",
  };
}

function localEvidence<T>(value: T | null): MtbTrailVulnerabilityEvidence<T> {
  return {
    value,
    source: value == null ? "unavailable" : "local-config",
  };
}

export function weightedMtbSoilMoisture(
  weather: MtbSoilMoistureLayers,
): number | null {
  const layers = [
    weather.soil_moisture_0_1cm,
    weather.soil_moisture_1_3cm,
    weather.soil_moisture_3_9cm,
    weather.soil_moisture_9_27cm,
  ];

  let weightedSum = 0;
  let weightSum = 0;

  for (let index = 0; index < layers.length; index++) {
    const moisture = layers[index];
    if (moisture !== null) {
      const weight = MTB_SOIL_MOISTURE_MODEL.layerWeights[index];
      weightedSum += moisture * weight;
      weightSum += weight;
    }
  }

  return weightSum > 0 ? weightedSum / weightSum : null;
}

export function mtbSurfaceMoistureFactor(moisture: number): number {
  const { sweetSpot, curve } = MTB_SOIL_MOISTURE_MODEL;

  if (moisture >= sweetSpot.low && moisture <= sweetSpot.high) return 1;

  if (moisture < sweetSpot.low) {
    const distance = sweetSpot.low - moisture;
    return curve.dryFloor +
      (1 - curve.dryFloor) *
        Math.exp(-(distance * distance) / (2 * curve.drySigma * curve.drySigma));
  }

  const distance = moisture - sweetSpot.high;
  return curve.wetFloor +
    (1 - curve.wetFloor) *
      Math.exp(-(distance * distance) / (2 * curve.wetSigma * curve.wetSigma));
}

export function mtbSoilMoistureLabel(
  moisture: number | null | undefined,
): MtbSoilMoistureLabel | null {
  if (moisture == null) return null;

  const { sweetSpot, labels } = MTB_SOIL_MOISTURE_MODEL;
  if (moisture < labels.dustyBelow) return "Dusty";
  if (moisture < sweetSpot.low) return "Dry";
  if (moisture <= sweetSpot.high) return "Tacky";
  if (moisture < labels.wetFrom) return "Damp";
  if (moisture < labels.muddyFrom) return "Wet";
  if (moisture < labels.boggyFrom) return "Muddy";
  if (moisture < labels.waterloggedFrom) return "Boggy";
  return "Waterlogged";
}

export function mtbSoilMoistureColour(
  moisture: number | null | undefined,
): string {
  switch (mtbSoilMoistureLabel(moisture)) {
    case "Dusty": return "#92400e";
    case "Dry": return "#b45309";
    case "Tacky": return "#059669";
    case "Damp": return "#0ea5e9";
    case "Wet": return "#3b82f6";
    case "Muddy": return "#2563eb";
    case "Boggy": return "#1e40af";
    case "Waterlogged": return "#1e3a8a";
    default: return "#9ca3af";
  }
}
